import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import "./HighlightsModal.css";

const blankHighlight = {
    date: "",
    title: "",
    short_summary: "",
    description: "",
    full_detail: "",
    category: "",
    lodge: "",
    people: "",
    source_document: "",
    importance: "Notable",
    location: "",
    tags: "",
    media_path: "",
    picture_citation: "",
    key_masonic_facts: "",
    date_display_mode: "auto",
    is_public: true,
    display_on_homepage: false,
};

const importanceOptions = ["Major", "Notable", "Routine"];
const dateDisplayOptions = [
    { value: "auto", label: "Auto: year if 1 Jan" },
    { value: "exact", label: "Exact date" },
    { value: "year", label: "Year only" },
];
const categoryOptions = [
    "Public History",
    "Lodge Milestone",
    "Consecration",
    "Installation",
    "Anniversary",
    "Temple / Building",
    "Provincial History",
    "Meeting / Communication",
    "Degree / Ceremony",
    "Biography",
    "Charity / Fundraising",
    "Publication",
    "Photograph",
    "Minutes",
    "Research Note",
];

function toLocalDate(dateString) {
    if (!dateString) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";
    const adjusted = new Date(date.getTime() + 2 * 60 * 60 * 1000);
    return adjusted.toISOString().split("T")[0];
}

function normalizeHighlight(highlight) {
    return {
        ...blankHighlight,
        ...highlight,
        date: toLocalDate(highlight.date),
        short_summary: highlight.short_summary || highlight.description || "",
        full_detail: highlight.full_detail || highlight.description || "",
        importance: highlight.importance || "Notable",
        date_display_mode: ["auto", "exact", "year"].includes(highlight.date_display_mode) ? highlight.date_display_mode : "auto",
        is_public: highlight.is_public !== false,
        display_on_homepage: Boolean(highlight.display_on_homepage),
    };
}

function optionFromValue(value) {
    return value ? { value, label: value } : null;
}

function buildOptions(values) {
    return [...new Set(values.filter(Boolean))]
        .sort((a, b) => String(a).localeCompare(String(b)))
        .map((value) => ({ value, label: value }));
}

function HighlightsModal({ year, user, onClose }) {
    const [highlights, setHighlights] = useState([]);
    const [lodgeDirectoryOptions, setLodgeDirectoryOptions] = useState([]);
    const [formData, setFormData] = useState(blankHighlight);
    const [editingId, setEditingId] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [imageUploading, setImageUploading] = useState(false);
    const [imageUploadError, setImageUploadError] = useState("");
    const [filters, setFilters] = useState({
        year: year ? String(year) : "",
        category: "",
        lodge: "",
        importance: "",
        search: "",
    });

    const canManage = user?.role === "Admin" || user?.role === "Archivist";

    const fetchHighlights = useCallback(async () => {
        try {
            const res = await axios.get("/api/yearly-highlights", {
                params: { publicOnly: canManage ? "false" : "true" },
            });
            setHighlights(res.data.map(normalizeHighlight));
        } catch (error) {
            console.error("Error fetching highlights:", error);
        }
    }, [canManage]);

    useEffect(() => {
        fetchHighlights();
    }, [fetchHighlights]);

    useEffect(() => {
        let cancelled = false;

        axios.get("/api/resources/lodges")
            .then((res) => {
                if (cancelled) return;
                const lodgeNames = Array.isArray(res.data)
                    ? res.data.map((lodge) => lodge.name || lodge.label || lodge.description).filter(Boolean)
                    : [];
                setLodgeDirectoryOptions(buildOptions(lodgeNames));
            })
            .catch((error) => {
                console.error("Error fetching lodge options:", error);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const options = useMemo(() => {
        const savedLodges = buildOptions(highlights.map((highlight) => highlight.lodge));
        const lodgesByValue = new Map(
            [...lodgeDirectoryOptions, ...savedLodges].map((option) => [option.value, option])
        );

        return {
            years: buildOptions(highlights.map((highlight) => highlight.date?.slice(0, 4))),
            categories: buildOptions([...categoryOptions, ...highlights.map((highlight) => highlight.category)]),
            lodges: [...lodgesByValue.values()].sort((a, b) => a.label.localeCompare(b.label)),
            importance: importanceOptions.map((value) => ({ value, label: value })),
        };
    }, [highlights, lodgeDirectoryOptions]);

    const filteredHighlights = useMemo(() => {
        const search = filters.search.trim().toLowerCase();
        return highlights
            .filter((highlight) => !filters.year || highlight.date.startsWith(filters.year))
            .filter((highlight) => !filters.category || highlight.category === filters.category)
            .filter((highlight) => !filters.lodge || highlight.lodge === filters.lodge)
            .filter((highlight) => !filters.importance || highlight.importance === filters.importance)
            .filter((highlight) => {
                if (!search) return true;
                return [
                    highlight.title,
                    highlight.short_summary,
                    highlight.description,
                    highlight.full_detail,
                    highlight.category,
                    highlight.lodge,
                    highlight.people,
                    highlight.location,
                    highlight.tags,
                    highlight.picture_citation,
                    highlight.key_masonic_facts,
                    highlight.source_document,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase()
                    .includes(search);
            })
            .sort((a, b) => {
                const dateCompare = String(b.date).localeCompare(String(a.date));
                return dateCompare || String(a.title).localeCompare(String(b.title));
            });
    }, [filters, highlights]);

    const groupedHighlights = useMemo(() => {
        return filteredHighlights.reduce((groups, highlight) => {
            const highlightYear = highlight.date?.slice(0, 4) || "Undated";
            if (!groups[highlightYear]) groups[highlightYear] = [];
            groups[highlightYear].push(highlight);
            return groups;
        }, {});
    }, [filteredHighlights]);

    function handleFilterChange(name, value) {
        setFilters((current) => ({ ...current, [name]: value || "" }));
    }

    function handleInputChange(event) {
        const { name, value, type, checked } = event.target;
        setFormData((current) => ({
            ...current,
            [name]: type === "checkbox" ? checked : value,
        }));
    }

    function openEditor(highlight = null) {
        if (highlight) {
            setFormData(normalizeHighlight(highlight));
            setEditingId(highlight.id);
        } else {
            setFormData({
                ...blankHighlight,
                date: new Date().toISOString().slice(0, 10),
            });
            setEditingId(null);
        }
        setEditorOpen(true);
    }

    function closeEditor() {
        setEditorOpen(false);
        setEditingId(null);
        setFormData(blankHighlight);
        setImageUploading(false);
        setImageUploadError("");
    }

    async function handleImageUpload(event) {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        const uploadData = new FormData();
        uploadData.append("image", file);
        setImageUploading(true);
        setImageUploadError("");

        try {
            const res = await axios.post("/api/yearly-highlights/upload-image", uploadData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const mediaPath = res.data?.mediaPath;
            if (!mediaPath) throw new Error("Upload did not return a media path.");
            setFormData((current) => ({ ...current, media_path: mediaPath }));
        } catch (error) {
            console.error("Error uploading timeline image:", error);
            setImageUploadError(error.response?.data?.error || "Failed to upload image.");
        } finally {
            setImageUploading(false);
        }
    }

    function handleRemoveImage() {
        setFormData((current) => ({ ...current, media_path: "" }));
        setImageUploadError("");
    }

    async function handleSave(event) {
        event.preventDefault();
        const payload = {
            ...formData,
            year: formData.date ? Number(formData.date.slice(0, 4)) : year,
            description: formData.description || formData.short_summary,
            short_summary: formData.short_summary || formData.description,
            full_detail: formData.full_detail || formData.description || formData.short_summary,
        };

        try {
            if (editingId) {
                await axios.put(`/api/yearly-highlights/${editingId}`, payload);
            } else {
                await axios.post("/api/yearly-highlights", payload);
            }
            await fetchHighlights();
            closeEditor();
        } catch (error) {
            console.error("Error saving highlight:", error);
            alert("Failed to save timeline entry.");
        }
    }

    async function handleDelete(id) {
        if (!window.confirm("Delete this timeline entry?")) return;
        try {
            await axios.delete(`/api/yearly-highlights/${id}`);
            await fetchHighlights();
        } catch (error) {
            console.error("Error deleting highlight:", error);
            alert("Failed to delete timeline entry.");
        }
    }

    return (
        <div className="highlights-modal" role="dialog" aria-modal="true" aria-labelledby="highlights-title">
            <div className="highlights-modal-content">
                <button className="highlights-close-btn" onClick={onClose} aria-label="Close highlights">
                    &times;
                </button>

                <header className="highlights-header">
                    <div>
                        <p className="highlights-eyebrow">Connaught Research Lodge Archive</p>
                        <h2 id="highlights-title">{canManage ? "Timeline Builder" : "Interactive Timeline"}</h2>
                        <p>
                            {canManage
                                ? "Create, edit, draft, publish, feature, and source timeline entries."
                                : "Browse key moments by year, lodge, people, category, and source."}
                        </p>
                    </div>
                    <div className="highlights-count">
                        <strong>{filteredHighlights.length}</strong>
                        <span>{filteredHighlights.length === 1 ? "entry" : "entries"}</span>
                    </div>
                </header>

                <section className="highlights-toolbar" aria-label="Highlight filters">
                    <input
                        type="search"
                        value={filters.search}
                        onChange={(event) => handleFilterChange("search", event.target.value)}
                        placeholder="Search title, people, lodge, source, tags..."
                    />
                    <Select
                        value={optionFromValue(filters.year)}
                        onChange={(option) => handleFilterChange("year", option?.value)}
                        options={options.years}
                        isClearable
                        placeholder="Year"
                        className="highlights-select"
                        classNamePrefix="highlight-select"
                    />
                    <Select
                        value={optionFromValue(filters.category)}
                        onChange={(option) => handleFilterChange("category", option?.value)}
                        options={options.categories}
                        isClearable
                        placeholder="Category"
                        className="highlights-select"
                        classNamePrefix="highlight-select"
                    />
                    <Select
                        value={optionFromValue(filters.lodge)}
                        onChange={(option) => handleFilterChange("lodge", option?.value)}
                        options={options.lodges}
                        isClearable
                        placeholder="Lodge"
                        className="highlights-select"
                        classNamePrefix="highlight-select"
                    />
                    <Select
                        value={optionFromValue(filters.importance)}
                        onChange={(option) => handleFilterChange("importance", option?.value)}
                        options={options.importance}
                        isClearable
                        placeholder="Importance"
                        className="highlights-select"
                        classNamePrefix="highlight-select"
                    />
                    <button
                        type="button"
                        className="highlights-secondary-btn"
                        onClick={() => setFilters({ year: "", category: "", lodge: "", importance: "", search: "" })}
                    >
                        Clear
                    </button>
                    {canManage && (
                        <button type="button" className="highlights-primary-btn" onClick={() => openEditor()}>
                            Add Timeline Entry
                        </button>
                    )}
                </section>

                <section className="highlights-timeline">
                    {Object.keys(groupedHighlights).length === 0 && (
                        <div className="highlights-empty">No timeline entries match the current filters.</div>
                    )}

                    {Object.entries(groupedHighlights).map(([groupYear, group]) => (
                        <div className="highlights-year-group" key={groupYear}>
                            <div className="highlights-year-marker">{groupYear}</div>
                            <div className="highlights-year-items">
                                {group.map((highlight) => {
                                    const isExpanded = expandedId === highlight.id;
                                    const tags = String(highlight.tags || "")
                                        .split(",")
                                        .map((tag) => tag.trim())
                                        .filter(Boolean);
                                    const keyFacts = String(highlight.key_masonic_facts || "")
                                        .split(/\r?\n/)
                                        .map((fact) => fact.trim())
                                        .filter(Boolean);

                                    return (
                                        <article className="highlight-card" key={highlight.id}>
                                            <div className="highlight-date">
                                                <strong>{highlight.date || "Undated"}</strong>
                                                <span>{highlight.importance}</span>
                                            </div>

                                            {highlight.media_path && (
                                                <img src={highlight.media_path} alt="" className="highlight-media" />
                                            )}

                                            <div className="highlight-body">
                                                <div className="highlight-card-head">
                                                    <div>
                                                        <h3>{highlight.title}</h3>
                                                        <div className="highlight-chips">
                                                            {highlight.category && <span>{highlight.category}</span>}
                                                            {highlight.lodge && <span>{highlight.lodge}</span>}
                                                            {highlight.location && <span>{highlight.location}</span>}
                                                            {!highlight.is_public && <span>Private</span>}
                                                            {highlight.display_on_homepage && <span>Featured</span>}
                                                        </div>
                                                    </div>
                                                </div>

                                                <p className="highlight-summary">
                                                    {highlight.short_summary || highlight.description}
                                                </p>

                                                {isExpanded && (
                                                    <div className="highlight-detail">
                                                        {(highlight.full_detail || highlight.description) && (
                                                            <p>{highlight.full_detail || highlight.description}</p>
                                                        )}
                                                        <dl>
                                                            {highlight.people && (
                                                                <>
                                                                    <dt>People</dt>
                                                                    <dd>{highlight.people}</dd>
                                                                </>
                                                            )}
                                                            {highlight.source_document && (
                                                                <>
                                                                    <dt>Source</dt>
                                                                    <dd>{highlight.source_document}</dd>
                                                                </>
                                                            )}
                                                            {highlight.picture_citation && (
                                                                <>
                                                                    <dt>Picture</dt>
                                                                    <dd>{highlight.picture_citation}</dd>
                                                                </>
                                                            )}
                                                            {keyFacts.length > 0 && (
                                                                <>
                                                                    <dt>Key facts</dt>
                                                                    <dd>
                                                                        <ul className="highlight-facts-list">
                                                                            {keyFacts.map((fact) => <li key={fact}>{fact}</li>)}
                                                                        </ul>
                                                                    </dd>
                                                                </>
                                                            )}
                                                            {tags.length > 0 && (
                                                                <>
                                                                    <dt>Tags</dt>
                                                                    <dd>{tags.join(", ")}</dd>
                                                                </>
                                                            )}
                                                        </dl>
                                                    </div>
                                                )}

                                                <div className="highlight-actions">
                                                    <button
                                                        type="button"
                                                        className="highlights-secondary-btn"
                                                        onClick={() => setExpandedId(isExpanded ? null : highlight.id)}
                                                    >
                                                        {isExpanded ? "Hide Details" : "View Details"}
                                                    </button>
                                                    {canManage && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="highlights-secondary-btn"
                                                                onClick={() => openEditor(highlight)}
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="highlights-danger-btn"
                                                                onClick={() => handleDelete(highlight.id)}
                                                            >
                                                                Delete
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </section>

                {editorOpen && canManage && (
                    <div className="highlight-editor-backdrop" role="dialog" aria-modal="true" aria-labelledby="highlight-editor-title">
                        <form className="highlight-editor" onSubmit={handleSave}>
                            <header>
                                <div>
                                    <p className="highlights-eyebrow">{editingId ? "Edit record" : "New record"}</p>
                                    <h3 id="highlight-editor-title">{editingId ? "Edit Timeline Entry" : "Add Timeline Entry"}</h3>
                                </div>
                                <button type="button" className="highlights-close-btn inline" onClick={closeEditor} aria-label="Close editor">
                                    &times;
                                </button>
                            </header>

                            <div className="highlight-form-grid">
                                <label>
                                    Date
                                    <input type="date" name="date" value={formData.date} onChange={handleInputChange} required />
                                </label>
                                <label>
                                    Importance
                                    <select name="importance" value={formData.importance} onChange={handleInputChange}>
                                        {importanceOptions.map((option) => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    Date display
                                    <select name="date_display_mode" value={formData.date_display_mode} onChange={handleInputChange}>
                                        {dateDisplayOptions.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="wide">
                                    Title
                                    <input type="text" name="title" value={formData.title} onChange={handleInputChange} required />
                                </label>
                                <label>
                                    Category
                                    <input
                                        type="text"
                                        name="category"
                                        list="historical-highlight-category-options"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        placeholder="Public History, Installation, Research Note..."
                                    />
                                    <datalist id="historical-highlight-category-options">
                                        {categoryOptions.map((option) => (
                                            <option key={option} value={option} />
                                        ))}
                                    </datalist>
                                </label>
                                <label>
                                    Lodge
                                    <Select
                                        value={optionFromValue(formData.lodge)}
                                        onChange={(option) => setFormData((current) => ({ ...current, lodge: option?.value || "" }))}
                                        options={options.lodges}
                                        isClearable
                                        placeholder="Select lodge"
                                        className="highlight-form-select"
                                        classNamePrefix="highlight-select"
                                    />
                                </label>
                                <label>
                                    People involved
                                    <input type="text" name="people" value={formData.people} onChange={handleInputChange} placeholder="Names separated by commas" />
                                </label>
                                <label>
                                    Location
                                    <input type="text" name="location" value={formData.location} onChange={handleInputChange} />
                                </label>
                                <label className="wide">
                                    Short summary
                                    <textarea name="short_summary" value={formData.short_summary} onChange={handleInputChange} rows="3" />
                                </label>
                                <label className="wide">
                                    Full detail
                                    <textarea name="full_detail" value={formData.full_detail} onChange={handleInputChange} rows="5" />
                                </label>
                                <label className="wide">
                                    Source document
                                    <input type="text" name="source_document" value={formData.source_document} onChange={handleInputChange} placeholder="Document title, archive ID, or path" />
                                </label>
                                <label className="wide">
                                    Key Masonic facts
                                    <textarea
                                        name="key_masonic_facts"
                                        value={formData.key_masonic_facts}
                                        onChange={handleInputChange}
                                        rows="3"
                                        placeholder="One fact per line, e.g. First degree conferred, warrant date, constitution, ceremony, lodge type..."
                                    />
                                </label>
                                <label>
                                    Tags
                                    <input type="text" name="tags" value={formData.tags} onChange={handleInputChange} placeholder="comma, separated, tags" />
                                </label>
                                <label>
                                    Media path
                                    <input type="text" name="media_path" value={formData.media_path} onChange={handleInputChange} placeholder="/web_images/example.jpg" />
                                </label>
                                <label className="wide">
                                    Picture citation
                                    <input
                                        type="text"
                                        name="picture_citation"
                                        value={formData.picture_citation}
                                        onChange={handleInputChange}
                                        placeholder="Image credit, source, archive reference, or note if digitally created"
                                    />
                                </label>
                                <div className="highlight-image-upload-panel">
                                    <span>Timeline image</span>
                                    <div className="highlight-image-upload-actions">
                                        <label className="highlights-secondary-btn highlight-upload-button">
                                            {imageUploading ? "Uploading..." : "Upload Image"}
                                            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleImageUpload} disabled={imageUploading} />
                                        </label>
                                        {formData.media_path && (
                                            <>
                                                <a href={formData.media_path} target="_blank" rel="noreferrer">Open image</a>
                                                <button type="button" className="highlight-remove-image-button" onClick={handleRemoveImage}>
                                                    Remove image
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    {imageUploadError && <p className="highlight-image-upload-error">{imageUploadError}</p>}
                                    {formData.media_path && (
                                        <div className="highlight-image-preview">
                                            <img src={formData.media_path} alt="" />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="highlight-checks">
                                <label>
                                    <input type="checkbox" name="is_public" checked={formData.is_public} onChange={handleInputChange} />
                                    Public
                                </label>
                                <label>
                                    <input type="checkbox" name="display_on_homepage" checked={formData.display_on_homepage} onChange={handleInputChange} />
                                    Feature in Research & Heritage
                                </label>
                            </div>

                            <footer>
                                <button type="button" className="highlights-secondary-btn" onClick={closeEditor}>Cancel</button>
                                <button type="submit" className="highlights-primary-btn">{editingId ? "Save Changes" : "Create Timeline Entry"}</button>
                            </footer>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
}

export default HighlightsModal;
